import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/lib/database.types';

type RecurringTransaction = Database['public']['Tables']['recurring_transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
};

type RecurringTransactionInsert = {
  description: string;
  amount: number;
  type: 'income' | 'expense';
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  start_date?: string;
  end_date?: string | null;
  category_id?: string | null;
  account_id?: string | null;
};

type Frequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

// Calculate next occurrence based on frequency
function calculateNextOccurrence(currentDate: Date, frequency: Frequency): Date {
  const next = new Date(currentDate);

  switch (frequency) {
    case 'daily':
      next.setDate(next.getDate() + 1);
      break;
    case 'weekly':
      next.setDate(next.getDate() + 7);
      break;
    case 'monthly':
      next.setMonth(next.getMonth() + 1);
      break;
    case 'yearly':
      next.setFullYear(next.getFullYear() + 1);
      break;
  }

  return next;
}

// Parse a date string as a local date (avoids UTC timezone shift)
function parseLocalDate(dateStr: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const datePart = dateStr.substring(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [y, m, d] = datePart.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(dateStr);
}

function toLocalDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Get upcoming occurrences for the next N days
function getUpcomingOccurrences(
  recurring: RecurringTransaction[],
  daysAhead: number = 30
): Array<{ recurring: RecurringTransaction; date: Date }> {
  const now = new Date();
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + daysAhead);

  const upcoming: Array<{ recurring: RecurringTransaction; date: Date }> = [];

  recurring.forEach((r) => {
    if (!r.is_active) return;

    let nextDate = parseLocalDate(r.next_occurrence);
    const rEndDate = r.end_date ? parseLocalDate(r.end_date) : null;

    // Get all occurrences within the range
    while (nextDate <= endDate) {
      if (rEndDate && nextDate > rEndDate) break;

      if (nextDate >= now) {
        upcoming.push({ recurring: r, date: new Date(nextDate) });
      }

      nextDate = calculateNextOccurrence(nextDate, r.frequency);
    }
  });

  // Sort by date
  return upcoming.sort((a, b) => a.date.getTime() - b.date.getTime());
}

export function useRecurringTransactions() {
  const { user } = useAuth();
  const [recurringTransactions, setRecurringTransactions] = useState<RecurringTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRecurringTransactions = useCallback(async () => {
    if (!user) {
      setRecurringTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('recurring_transactions')
        .select(
          `
          *,
          category:categories(*),
          account:accounts(*)
        `
        )
        .eq('user_id', user.id)
        .order('next_occurrence', { ascending: true });

      if (fetchError) throw fetchError;

      setRecurringTransactions(data || []);
    } catch (err) {
      console.error('Error loading recurring transactions:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadRecurringTransactions();
  }, [loadRecurringTransactions]);

  const createRecurringTransaction = async (data: RecurringTransactionInsert) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const startDate = data.start_date ? parseLocalDate(data.start_date) : new Date();
      const startDateStr = toLocalDateStr(startDate);

      const { error: insertError } = await supabase.from('recurring_transactions').insert({
        ...data,
        user_id: user.id,
        start_date: startDateStr,
        next_occurrence: startDateStr,
        is_active: true,
      });

      if (insertError) throw insertError;

      await loadRecurringTransactions();
      window.dispatchEvent(new CustomEvent('achievements-need-refresh'));
      return { success: true };
    } catch (err) {
      console.error('Error creating recurring transaction:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  const updateRecurringTransaction = async (
    id: string,
    data: Partial<RecurringTransactionInsert>
  ) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const { error: updateError } = await supabase
        .from('recurring_transactions')
        .update({
          ...data,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      await loadRecurringTransactions();
      return { success: true };
    } catch (err) {
      console.error('Error updating recurring transaction:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  const deleteRecurringTransaction = async (id: string) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const { error: deleteError } = await supabase
        .from('recurring_transactions')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      await loadRecurringTransactions();
      return { success: true };
    } catch (err) {
      console.error('Error deleting recurring transaction:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const { error: updateError } = await supabase
        .from('recurring_transactions')
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      await loadRecurringTransactions();
      return { success: true };
    } catch (err) {
      console.error('Error toggling recurring transaction:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  // Get upcoming transactions for the next N days
  const getUpcoming = useCallback(
    (daysAhead: number = 30) => {
      return getUpcomingOccurrences(recurringTransactions, daysAhead);
    },
    [recurringTransactions]
  );

  // Filter active recurring transactions
  const activeRecurring = recurringTransactions.filter((r) => r.is_active);

  return {
    recurringTransactions,
    activeRecurring,
    loading,
    error,
    loadRecurringTransactions,
    createRecurringTransaction,
    updateRecurringTransaction,
    deleteRecurringTransaction,
    toggleActive,
    getUpcoming,
  };
}

export type { RecurringTransaction, RecurringTransactionInsert, Frequency };
